import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { EmptyComponent } from '@design/layout/empty/empty.component';
import { UiVersionRouteGuard } from '@security/authorization/ui-version.route-guard';
import { environment } from '@env/environment';
import { AnonymousRouteGuard } from '@security/authorization/anonymous.route-guard';
import { ForbiddenComponent } from '@security/authorization/forbidden/forbidden.component';
import { NoPermissionsComponent } from '@security/authorization/no-permissions/no-permissions.component';
import { ResourceNotFoundComponent } from '@design/layout/resource-not-found/resource-not-found.component';
import { payrollModalRoutes } from './payrolls/payroll-modal-routes';
import { employeeHubModalRoutes } from './employee-hub/employee-hub-modal-routes';
import { SignInComponent } from './app-layout/auth-n/sign-in/sign-in.component';
import { IqLockedComponent } from '@security/authorization/IqLocked/IqLocked.component';
import { integrationsModalRoutes } from '@app/integrations-admin/integrations-modal-routes';
import { ResetPasswordComponent } from '@app/app-layout/auth-n/reset-password/reset-password.component';
import { extrasStateResolver } from '@utils/route-utils';
import { employeeLeaveModalRoutes } from '@app/employees/employee-leave/employee-leave-modal-routes';
import { bureauModalRoutes } from '@app/bureau/bureau-modal-routes';
import { calendarModalRoutes } from '@design/components/events-calendar/calendar-modal-routes';
import { adminHubModalRoutes } from '@app/admin-hub/admin-hub-modal-routes';
import { customReportTypesModalRoutes } from '@app/custom-reports-admin/custom-report-types-modal-routes';
import { SsoLandingComponent } from '@auth-n/sso-landing/sso-landing.component';
import { employeesModalRoutes } from '@app/employees/employees-modal-routes';
import { organisationModalRoutes } from '@organisations/organisation-modal-routes';
import { accessControlModalRoutes } from '@app/access-control/access-control-modal-routes';
import { advancedReportsModalRoutes } from '@app/advanced-reports/dynamic-reporting/advanced-reports-modal-routes';
import { userModalRoutes } from '@security/users/user-modal-routes';
import { designSystemModalRoutes } from '@app/design-system/design-system-modal-routes';
import { positionsModalRoutes } from '@app/positions/positions-modal-routes';
import { paymentsHubModalRoutes } from '@app/payments-hub/payments-hub-modal-routes';
import { PensionManagedBy } from '@app/pension-connect/models/pension-connect.constants';
import { LogoutComponent } from '@security/active-security/logout.component';
import { CintraPayrollsAccessRouteGuard } from '@security/authorization/cintra-payrolls-access.route-guard';
import { ContainerQueryTestComponent } from '../component-library/layout/content/test/container-query-test.component';
import { FeatureFlagEnabledRouteGuard, getFeatureFlagKey } from '@security/authorization/feature-flag-enabled.route-guard';
import { pensionConnectModalRoutes } from './pension-connect/pension-connect-modal-routes';
import { hrLogModalRoutes } from '@app/hrlog/hrlog-modal-routes';
import { DomainRouteGuard } from '@domain/domain-route.guard';
import { payrollRouteData, PayrollFeatureRouteData } from '@app/cintra-product';
import {
  OrganisationAuthorisedRouteGuard,
  getAnyOrganisationPermissions
} from '@security/authorization/organisation-authorised.route-guard';
import { PayrollLockedRouteGuard } from '@app/payrolls/payroll-locked/payroll-locked.route-guard';
import { BulkImportPayrollUserRouteGuard } from '@security/authorization/bulk-import-payroll-user.route-guard';
import { BULK_IMPORT_PERMISSIONS } from '@app/bulk-import/hub/bulk-import-hub-catalog';

const routes: Routes = [
  {
    path: '',
    canActivateChild: environment.uiVersionCheckEnabled ? [UiVersionRouteGuard, DomainRouteGuard] : [DomainRouteGuard],
    children: [
      {
        path: 'container-query-test',
        component: ContainerQueryTestComponent,
        data: {
          cintraProduct: 'Payroll'
        }
      },
      { path: 'redirect', component: EmptyComponent, data: { allowAnyDomain: true } },
      { path: 'forbidden', component: ForbiddenComponent, data: { allowAnyDomain: true } },
      { path: 'locked', component: IqLockedComponent, data: { allowAnyDomain: true } },
      { path: 'no-permissions', component: NoPermissionsComponent, data: { allowAnyDomain: true } },
      { path: 'logout', component: LogoutComponent, data: { allowAnyDomain: true, skipAuthCheck: true } },
      {
        path: 'under-maintenance',
        data: { allowAnyDomain: true },
        loadChildren: () => import('../framework/under-maintenance/under-maintenance.module').then((m) => m.UnderMaintenanceModule)
      },
      {
        path: 'auth/sign-in',
        pathMatch: 'full',
        redirectTo: '/auth/(top:sign-in)'
      },
      {
        path: 'auth',
        canActivate: [AnonymousRouteGuard],
        data: { isRootDomainRoute: true },
        children: [
          {
            path: 'sign-in',
            component: SignInComponent,
            outlet: 'top',
            resolve: {
              extrasStateResolver
            }
          },
          {
            path: 'request-reset-password',
            component: ResetPasswordComponent,
            outlet: 'top'
          },
          {
            path: 'reset-password',
            component: ResetPasswordComponent,
            outlet: 'top'
          },
          {
            path: 'sso',
            component: SignInComponent,
            outlet: 'top',
            resolve: {
              extrasStateResolver
            }
          },
          {
            path: 'sso-callback',
            component: SsoLandingComponent,
            outlet: 'top',
            resolve: {
              extrasStateResolver
            }
          }
        ]
      },
      {
        path: 'auth/user-invitation/:id',
        redirectTo: '/user-invitations/(top:register/:id)'
      },
      {
        path: 'user-invitations',
        canActivate: [AnonymousRouteGuard],
        data: { isRootDomainRoute: true },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./security/user-invitations/user-invitation.module').then((m) => m.UserInvitationModule)
      },
      {
        path: '',
        data: { allowAnyDomain: true },
        loadChildren: () => import('./security/my-account/my-account.module').then((m) => m.MyAccountModule)
      },
      ...employeeHubModalRoutes,
      {
        path: 'employee-hub',
        loadChildren: () => import('./employee-hub/employee-hub.module').then((m) => m.EmployeeHubModule)
      },
      ...adminHubModalRoutes,
      ...employeeLeaveModalRoutes,
      {
        path: 'admin-hub',
        loadChildren: () => import('./admin-hub/admin-hub.module').then((m) => m.AdminHubModule)
      },
      {
        path: 'bulk-import/one-off-payments/:payrollId',
        canActivate: [
          BulkImportPayrollUserRouteGuard,
          CintraPayrollsAccessRouteGuard,
          FeatureFlagEnabledRouteGuard,
          OrganisationAuthorisedRouteGuard,
          PayrollLockedRouteGuard
        ],
        data: <PayrollFeatureRouteData>{
          ...payrollRouteData,
          ...getFeatureFlagKey('bulkImport'),
          ...getAnyOrganisationPermissions('EditOneOffPayments'),
          feature: 'BulkImport',
          importContext: 'oneOffPayment'
        },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./bulk-import/bulk-import.module').then((m) => m.BulkImportModule)
      },
      {
        path: 'bulk-import/new-starters/:payrollId',
        canActivate: [
          BulkImportPayrollUserRouteGuard,
          CintraPayrollsAccessRouteGuard,
          FeatureFlagEnabledRouteGuard,
          OrganisationAuthorisedRouteGuard,
          PayrollLockedRouteGuard
        ],
        data: <PayrollFeatureRouteData>{
          ...payrollRouteData,
          ...getFeatureFlagKey('bulkImport'),
          ...getAnyOrganisationPermissions('EditEmployees'),
          feature: 'BulkImport',
          importContext: 'newStarter'
        },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./bulk-import/bulk-import.module').then((m) => m.BulkImportModule)
      },
      {
        path: 'bulk-import/salary-and-allowances/:payrollId',
        canActivate: [
          BulkImportPayrollUserRouteGuard,
          CintraPayrollsAccessRouteGuard,
          FeatureFlagEnabledRouteGuard,
          OrganisationAuthorisedRouteGuard,
          PayrollLockedRouteGuard
        ],
        data: <PayrollFeatureRouteData>{
          ...payrollRouteData,
          ...getFeatureFlagKey('bulkImport'),
          ...getAnyOrganisationPermissions('EditRecurringPayments'),
          feature: 'BulkImport',
          importContext: 'salaryAndAllowance'
        },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./bulk-import/bulk-import.module').then((m) => m.BulkImportModule)
      },
      {
        path: 'bulk-import/one-off-payments/group/:groupId',
        canActivate: [BulkImportPayrollUserRouteGuard, FeatureFlagEnabledRouteGuard, OrganisationAuthorisedRouteGuard],
        data: <PayrollFeatureRouteData>{
          ...payrollRouteData,
          ...getFeatureFlagKey('bulkImport'),
          ...getAnyOrganisationPermissions('EditOneOffPayments'),
          feature: 'BulkImport',
          mode: 'group',
          importContext: 'oneOffPayment'
        },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./bulk-import/bulk-import.module').then((m) => m.BulkImportModule)
      },
      {
        path: 'bulk-import/salary-and-allowances/group/:groupId',
        canActivate: [BulkImportPayrollUserRouteGuard, FeatureFlagEnabledRouteGuard, OrganisationAuthorisedRouteGuard],
        data: <PayrollFeatureRouteData>{
          ...payrollRouteData,
          ...getFeatureFlagKey('bulkImport'),
          ...getAnyOrganisationPermissions('EditRecurringPayments'),
          feature: 'BulkImport',
          mode: 'group',
          importContext: 'salaryAndAllowance'
        },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./bulk-import/bulk-import.module').then((m) => m.BulkImportModule)
      },
      {
        path: 'bulk-import',
        canActivate: [BulkImportPayrollUserRouteGuard, FeatureFlagEnabledRouteGuard, OrganisationAuthorisedRouteGuard],
        data: <PayrollFeatureRouteData>{
          ...payrollRouteData,
          ...getFeatureFlagKey('bulkImport'),
          ...getAnyOrganisationPermissions(...BULK_IMPORT_PERMISSIONS),
          mode: 'hub',
          feature: 'BulkImport'
        },
        runGuardsAndResolvers: 'always',
        loadComponent: () => import('./bulk-import/hub/bulk-import-hub.component').then((m) => m.BulkImportHubComponent)
      },
      ...calendarModalRoutes,
      ...integrationsModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./integrations-admin/integrations.module').then((m) => m.IntegrationsModule)
      },
      ...customReportTypesModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./custom-reports-admin/custom-report-types.module').then((m) => m.CustomReportTypesModule)
      },
      ...positionsModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./positions/positions.module').then((p) => p.PositionsModule)
      },
      { path: 'payroll/hub', redirectTo: 'employers' },
      ...payrollModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./payrolls/payrolls.module').then((m) => m.PayrollsModule)
      },
      ...userModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('@security/users/user.module').then((m) => m.UserModule)
      },
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./employers/employer.module').then((m) => m.EmployerModule)
      },
      ...employeesModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./employees/employees.module').then((m) => m.EmployeesModule)
      },
      ...paymentsHubModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./payments-hub/payments-hub.module').then((m) => m.PaymentsHubModule)
      },
      ...organisationModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./organisations/organisation.module').then((m) => m.OrganisationModule)
      },
      ...accessControlModalRoutes,
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./access-control/access-control.module').then((m) => m.AccessControlModule)
      },
      {
        path: '',
        loadChildren: () => import('./access-control/bureaus/bureau-profiles.module').then((m) => m.BureauProfilesModule)
      },
      ...bureauModalRoutes,
      {
        path: 'bureau',
        loadChildren: () => import('./bureau/bureau.module').then((m) => m.BureauModule)
      },
      ...advancedReportsModalRoutes,
      {
        path: '',
        loadChildren: () => import('./advanced-reports/dynamic-reporting/advanced-reports.module').then((m) => m.AdvancedReportsModule)
      },
      ...hrLogModalRoutes,
      {
        path: 'hrlog',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./hrlog/cintrahrlog.module').then((m) => m.CintraHrLogModule)
      },
      ...pensionConnectModalRoutes,
      {
        path: 'pension-submissions',
        canActivate: [FeatureFlagEnabledRouteGuard],
        data: {
          ...getFeatureFlagKey('pensionConnect'),
          feature: 'Pension Connect',
          managedBy: PensionManagedBy.Employer
        },
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./pension-connect/pension-connect.module').then((m) => m.PensionConnectModule)
      },
      ...designSystemModalRoutes,
      {
        path: '',
        data: { allowAnyDomain: true },
        loadChildren: () => import('./design-system/design-system-docs.module').then((m) => m.DesignSystemDocsModule)
      },
      {
        path: '',
        data: { allowAnyDomain: true },
        loadChildren: () => import('./admin-area/admin-area.module').then((m) => m.AdminAreaModule)
      },
      {
        path: '',
        data: { allowAnyDomain: true },
        loadChildren: () => import('./test-support/test-support.module').then((m) => m.TestSupportModule)
      },
      {
        path: '',
        canActivate: [CintraPayrollsAccessRouteGuard],
        runGuardsAndResolvers: 'always',
        loadChildren: () => import('./messaging/messaging.module').then((m) => m.MessagingModule)
      },
      { path: '', component: EmptyComponent, data: { allowAnyDomain: true } },
      { path: '**', component: ResourceNotFoundComponent, data: { allowAnyDomain: true } }
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'always', onSameUrlNavigation: 'reload' })],
  exports: [RouterModule]
})
export class AppRoutingModule {}
